import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Expand, X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { projects } from "@/data/projects";

const EASE = [0.22, 1, 0.36, 1];

function ChromeBar() {
    return (
        <div className="flex items-center gap-1.5 bg-[#1B2026] px-4 py-2.5">
            <span className="h-2 w-2 rounded-full bg-white/15" />
            <span className="h-2 w-2 rounded-full bg-white/15" />
            <span className="h-2 w-2 rounded-full bg-white/15" />
        </div>
    );
}

function BrowserFrame({ src, alt, placeholder, previewUrl, testId, onExpand }) {
    // previewUrl is passed only for live projects (hover mini-preview)
    const [loaded, setLoaded] = useState(false);
    const [live, setLive] = useState(false);

    return (
        <div
            className="cursor-pointer overflow-hidden rounded-t-lg border-b border-white/10"
            onMouseEnter={() => {
                if (!previewUrl) return;
                setLoaded(true);
                setLive(true);
            }}
            onMouseLeave={() => setLive(false)}
            onClick={onExpand}
            data-testid={testId ? `${testId}-frame` : undefined}
        >
            <div className="relative">
                <ChromeBar />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-fog/0 transition-colors duration-300 group-hover:text-glacier">
                    <Expand size={13} />
                </span>
            </div>
            {src ? (
                <div className="relative aspect-[16/10] overflow-hidden bg-coal">
                    <img
                        src={src}
                        alt={alt}
                        loading="lazy"
                        className={`absolute inset-0 h-full w-full object-cover object-left-top transition-[opacity,transform] duration-700 ease-out group-hover:scale-[1.03] ${
                            live ? "opacity-0" : "opacity-100"
                        }`}
                    />
                    {loaded && (
                        <iframe
                            data-testid={testId}
                            src={previewUrl}
                            title={alt}
                            loading="lazy"
                            tabIndex={-1}
                            aria-hidden="true"
                            className={`pointer-events-none absolute left-0 top-0 h-[400%] w-[400%] origin-top-left scale-[0.25] border-0 transition-opacity duration-700 ${
                                live ? "opacity-100" : "opacity-0"
                            }`}
                        />
                    )}
                </div>
            ) : (
                <div className="flex aspect-[16/10] items-center justify-center bg-coal">
                    <span className="font-display text-5xl font-medium text-glacier/40">{placeholder}</span>
                </div>
            )}
        </div>
    );
}

function ModalShell({ onClose, children, testId }) {
    useEffect(() => {
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    return (
        <motion.div
            data-testid={testId}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/90 p-4 backdrop-blur-md sm:p-8"
        >
            <motion.div
                initial={{ opacity: 0, y: 40, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 24, scale: 0.97 }}
                transition={{ duration: 0.45, ease: EASE }}
                onClick={(e) => e.stopPropagation()}
                className="flex h-full max-h-[85vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-white/10 bg-coal shadow-2xl"
            >
                {children}
            </motion.div>
        </motion.div>
    );
}

function ModalHeader({ title, onClose }) {
    return (
        <div className="flex items-center justify-between border-b border-white/10 bg-[#1B2026] px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-1.5">
                <span className="h-2 w-2 shrink-0 rounded-full bg-white/15" />
                <span className="h-2 w-2 shrink-0 rounded-full bg-white/15" />
                <span className="h-2 w-2 shrink-0 rounded-full bg-white/15" />
                <span className="ml-3 truncate font-mono text-[10px] sm:text-xs tracking-wider text-fog">
                    {title}
                </span>
            </div>
            <button
                data-testid="preview-modal-close"
                onClick={onClose}
                aria-label="Close"
                className="ml-4 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border border-white/10 text-fog transition-colors duration-300 hover:border-glacier hover:text-glacier"
            >
                <X size={14} />
            </button>
        </div>
    );
}

function LiveModal({ project, onClose }) {
    return (
        <ModalShell onClose={onClose} testId="preview-modal">
            <ModalHeader title={project.url.replace(/^https?:\/\//, "")} onClose={onClose} />
            <iframe
                data-testid="preview-modal-iframe"
                src={project.url}
                title={project.name}
                className="h-full w-full flex-1 border-0 bg-white"
            />
        </ModalShell>
    );
}

function GalleryModal({ project, onClose }) {
    const [index, setIndex] = useState(0);
    const total = project.gallery.length;

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") setIndex((i) => (i + 1) % total);
            if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + total) % total);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [total]);

    return (
        <ModalShell onClose={onClose} testId="gallery-modal">
            <ModalHeader title={project.name} onClose={onClose} />
            <div className="relative flex min-h-0 flex-1 items-center justify-center bg-ink/60">
                <AnimatePresence mode="wait">
                    <motion.img
                        key={index}
                        data-testid="gallery-main-image"
                        src={project.gallery[index]}
                        alt={`${project.name} ${index + 1}`}
                        initial={{ opacity: 0, scale: 0.985 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.01 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="h-full w-full object-contain"
                    />
                </AnimatePresence>
                <button
                    data-testid="gallery-prev"
                    onClick={() => setIndex((i) => (i - 1 + total) % total)}
                    aria-label="Previous"
                    className="absolute left-4 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-ink/70 text-ivory backdrop-blur transition-colors hover:border-glacier hover:text-glacier"
                >
                    <ChevronLeft size={18} />
                </button>
                <button
                    data-testid="gallery-next"
                    onClick={() => setIndex((i) => (i + 1) % total)}
                    aria-label="Next"
                    className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-ink/70 text-ivory backdrop-blur transition-colors hover:border-glacier hover:text-glacier"
                >
                    <ChevronRight size={18} />
                </button>
                <span
                    data-testid="gallery-counter"
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-white/10 bg-ink/70 px-3.5 py-1 font-mono text-[10px] tracking-[0.2em] text-fog backdrop-blur"
                >
                    {index + 1} / {total}
                </span>
            </div>
            <div className="flex gap-2 overflow-x-auto border-t border-white/10 bg-coal p-3">
                {project.gallery.map((src, i) => (
                    <button
                        key={src}
                        data-testid={`gallery-thumb-${i}`}
                        onClick={() => setIndex(i)}
                        className={`h-14 w-24 shrink-0 overflow-hidden rounded-sm border transition-[border-color,opacity] duration-300 ${
                            i === index ? "border-glacier" : "border-white/10 opacity-50 hover:opacity-90"
                        }`}
                    >
                        <img src={src} alt="" className="h-full w-full object-cover object-left-top" />
                    </button>
                ))}
            </div>
        </ModalShell>
    );
}

export default function PortfolioGrid({ t, lang }) {
    const [expandedId, setExpandedId] = useState(null);
    const expanded = projects.find((p) => p.id === expandedId) || null;

    return (
        <section id="portfolio" className="px-5 sm:px-8 py-24 sm:py-28">
            <div className="mx-auto max-w-6xl">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mb-14 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between"
                >
                    <div>
                        <p className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-glacier">
                            <span className="inline-block h-px w-10 bg-glacier/60" />
                            {t.portfolio.overline}
                        </p>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
                            {t.portfolio.title}
                        </h2>
                    </div>
                    <p className="max-w-sm text-sm sm:text-base leading-relaxed text-fog">{t.portfolio.sub}</p>
                </motion.div>

                <div data-testid="portfolio-grid-container" className="grid gap-8 md:grid-cols-2">
                    {projects.map((p, i) => {
                        const c = p[lang];
                        return (
                            <motion.article
                                key={p.id}
                                data-testid={p.testId}
                                initial={{ opacity: 0, y: 32 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: (i % 2) * 0.12, ease: EASE }}
                                className="group flex flex-col overflow-hidden rounded-lg border border-white/10 bg-coal transition-[border-color,transform] duration-500 hover:border-glacier/40 hover:-translate-y-1"
                            >
                                <BrowserFrame
                                    src={p.image}
                                    alt={p.name}
                                    placeholder={p.name.charAt(0)}
                                    previewUrl={p.live ? p.url : null}
                                    testId={`live-preview-${p.id}`}
                                    onExpand={() => setExpandedId(p.id)}
                                />

                                <div className="flex flex-1 flex-col p-6">
                                    <div className="flex items-center justify-between gap-3">
                                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-glacier">
                                            {c.category}
                                        </p>
                                        <span
                                            data-testid={`badge-status-${p.id}`}
                                            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-fog"
                                        >
                                            {p.gallery ? (
                                                <Images size={12} className="text-glacier" />
                                            ) : (
                                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                            )}
                                            {p.live ? t.portfolio.liveBadge : t.portfolio.conceptBadge}
                                        </span>
                                    </div>
                                    <h3 className="mt-3 font-display text-2xl font-medium tracking-tight text-ivory">
                                        {p.name}
                                    </h3>
                                    <p className="mt-2.5 flex-1 text-sm leading-relaxed text-fog">{c.description}</p>

                                    <div className="mt-5 flex flex-wrap gap-x-4 gap-y-1.5">
                                        {p.tags.map((tag) => (
                                            <span key={tag} className="font-mono text-[10px] uppercase tracking-wider text-fog/70">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>

                                    {p.live && p.url && (
                                        <a
                                            data-testid={`portfolio-project-external-link-${p.id}`}
                                            href={p.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-6 inline-flex items-center gap-2 border-t border-white/5 pt-5 font-display text-sm font-semibold uppercase tracking-widest text-glacier transition-colors duration-300 hover:text-ivory"
                                        >
                                            {t.portfolio.visit}
                                            <ArrowUpRight
                                                size={14}
                                                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                            />
                                        </a>
                                    )}
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </div>

            <AnimatePresence>
                {expanded &&
                    (expanded.gallery ? (
                        <GalleryModal project={expanded} onClose={() => setExpandedId(null)} />
                    ) : (
                        <LiveModal project={expanded} onClose={() => setExpandedId(null)} />
                    ))}
            </AnimatePresence>
        </section>
    );
}
